import { Forward } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { useGuildStore } from '@/store/guilds'
import { channelName, displayName, useSocialStore } from '@/store/social'
import { renderMarkdown, stickerImageUrl, type RenderOptions } from '@/lib/markdown'
import { formatDateTime } from '@/lib/format'
import { MessageFlags, type Message } from '@/types/api'
import { AttachmentView } from './AttachmentView'
import { EmbedCard } from './EmbedCard'
import { VoicePlayer } from './VoicePlayer'
import styles from './ForwardedBlock.module.css'

/**
 * Пересланное сообщение: «Переслано», копия текста, файлов, превью и стикеров и откуда оно — канал или беседа
 * со ссылкой на исходное, если оно доступно. Автора исходного Discord не показывает — и мы не показываем.
 */
export function ForwardedBlock({ message, render, onRemoveEmbed }: { message: Message; render: RenderOptions; onRemoveEmbed?: (() => void) | undefined }) {
  const snap = message.message_snapshots?.[0]?.message
  const ref = message.message_reference
  const guildId = ref?.guild_id
  const guild = useGuildStore((s) => (guildId ? s.guilds[guildId] : undefined))
  const channel = useGuildStore((s) => (guildId && ref ? s.channels[guildId]?.[ref.channel_id] : undefined))
  const dm = useSocialStore((s) => (!guildId && ref ? s.channels[ref.channel_id] : undefined))
  const openGuild = useAppStore((s) => s.openGuild)
  const setView = useAppStore((s) => s.setView)
  const setJumpTarget = useAppStore((s) => s.setJumpTarget)
  if (!snap || !ref) return null

  // имена упомянутых — из копии: в канале назначения этих людей может и не быть
  const mentioned = new Map(snap.mentions.map((u) => [u.id, displayName(u)]))
  const opts: RenderOptions = { ...render, userName: (id) => mentioned.get(id) ?? render.userName?.(id) }
  const where = channel ? `#${channel.name ?? ''}${guild ? ` · ${guild.name}` : ''}` : dm ? channelName(dm) : null
  const jump = where
    ? () => {
        if (guildId) openGuild(guildId, ref.channel_id)
        else setView({ kind: 'home', channelId: ref.channel_id })
        setJumpTarget({ channelId: ref.channel_id, messageId: ref.message_id })
      }
    : undefined
  const voice = ((snap.flags ?? 0) & MessageFlags.IsVoiceMessage) !== 0
  // превью скрыты в исходном или у пересланного: автором, модератором или без EMBED_LINKS в этом канале
  const embeds = ((snap.flags ?? 0) | (message.flags ?? 0)) & MessageFlags.SuppressEmbeds ? [] : snap.embeds ?? []
  return (
    <div className={styles.forward}>
      <span className={styles.label}><Forward size={14} aria-hidden="true" /> Переслано</span>
      {snap.content ? <div className={styles.content}>{renderMarkdown(snap.content, opts)}</div> : null}
      {snap.attachments?.length ? (voice && snap.attachments[0] ? <VoicePlayer attachment={snap.attachments[0]} /> : <AttachmentView attachments={snap.attachments} />) : null}
      {snap.sticker_items?.length ? (
        <div className={styles.stickers}>
          {snap.sticker_items.map((s) => <img key={s.id} src={stickerImageUrl(s)} alt={s.name} data-tooltip={s.name} className={styles.sticker} loading="lazy" draggable={false} />)}
        </div>
      ) : null}
      {embeds.map((e, i) => <EmbedCard key={i} embed={e} onRemove={onRemoveEmbed} render={opts} />)}
      <span className={styles.source}>
        {where && jump ? <button type="button" className={styles.link} onClick={jump}>{where}</button> : <span>из недоступного вам канала</span>}
        <span aria-hidden="true"> · </span>
        <time dateTime={snap.timestamp}>{formatDateTime(new Date(snap.timestamp))}</time>
      </span>
    </div>
  )
}
